import {
  Habit,
  ID,
  RepeatingTask,
  Task,
  TaskType,
  Timeslot,
  Timestamp,
  UpdateTask,
  WeekDays,
} from '@griply/types';
import { addDays, endOfDay, isAfter, isBefore } from 'date-fns';

import {
  createPlannedOccurrenceForDate,
  deletePlannedOccurrenceForDate,
  getScheduleEndDate,
} from './schedules';

export type GetTaskRescheduleUpdateOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  isDeadline?: boolean;
  originalDate?: Timestamp;
  occurrenceId?: ID;
};

// A todo's hard deadline (deadlineDeadline) lands it in the Overdue section once
// it passes the same threshold the deadline grouping uses (end of yesterday).
// When such a task is rescheduled, its hard deadline moves with it.
const isDeadlineDeadlineOverdue = (task: Task, today: Timestamp): boolean =>
  task.type === TaskType.Task &&
  !!task.deadlineDeadline &&
  !isAfter(task.deadlineDeadline, endOfDay(addDays(today, -1)));

export const getTaskRescheduleUpdate = (
  task: Task,
  date: Timestamp | null,
  timeslot: Timeslot | null,
  options: GetTaskRescheduleUpdateOptions,
): UpdateTask => {
  const scheduledTask =
    task.type !== TaskType.Task ? (task as Habit | RepeatingTask) : null;

  // unset: remove only the occurrence on the originalDate for scheduled tasks,
  // clear the deadline for todos.
  if (!date) {
    const { originalDate } = options;
    if (!scheduledTask || !originalDate) {
      return {
        id: task.id,
        endStrategy: null,
        timeslot: null,
        ...(isDeadlineDeadlineOverdue(task, options.today) && {
          deadlineDeadline: null,
        }),
      };
    }

    const schedules = scheduledTask.schedules
      .slice()
      .sort(
        (scheduleA, scheduleB) =>
          scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
      )
      .map((schedule, index, arr) => {
        const prevScheduleEnd = getScheduleEndDate(arr[index - 1]);
        const scheduleEnd = getScheduleEndDate(schedule);
        if (
          (prevScheduleEnd && isBefore(originalDate, prevScheduleEnd)) ||
          (scheduleEnd && isAfter(originalDate, scheduleEnd))
        ) {
          return schedule;
        }
        return deletePlannedOccurrenceForDate(schedule, originalDate, {
          weekStartsOn: options.weekStartsOn,
          today: options.today,
        });
      });

    return { id: task.id, schedules };
  }

  // deadlineDeadline only applies to todos; habits/repeating fall through to
  // the schedule update instead.
  if (options.isDeadline && task.type === TaskType.Task) {
    return { id: task.id, deadlineDeadline: endOfDay(date) };
  }

  if (scheduledTask) {
    const schedules = createPlannedOccurrenceForDate(
      scheduledTask.schedules,
      date,
      {
        timeslot,
        today: options.today,
        weekStartsOn: options.weekStartsOn,
        originalDate: options.originalDate,
        id: options.occurrenceId,
      },
    );

    return { id: task.id, schedules };
  }

  return {
    id: task.id,
    endStrategy: { completionCount: null, deadline: endOfDay(date) },
    timeslot,
    ...(isDeadlineDeadlineOverdue(task, options.today) && {
      deadlineDeadline: endOfDay(date),
    }),
  };
};
